import type { CSSProperties } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTextForms } from "@/api/admin/analytics";
import { AdminErrorState } from "@/components/admin/AdminErrorState";
import { formatNumber } from "@/lib/format";
import { i18n } from "@/i18n";

const subStyle: CSSProperties = {
  fontFamily: "var(--font-body)", fontSize: 12, color: "var(--d-fg-muted)",
};
const cardStyle: CSSProperties = {
  background: "var(--d-ink-2)",
  border: "1px solid var(--d-line)",
  borderRadius: 22,
  padding: 16,
  overflowX: "auto",
};
const cardTitleStyle: CSSProperties = {
  fontFamily: "var(--font-display)", fontSize: 16, color: "var(--d-fg)", marginBottom: 10,
};
const tableStyle: CSSProperties = { width: "100%", borderCollapse: "collapse" };
const thStyle: CSSProperties = {
  fontFamily: "var(--font-body)",
  fontSize: 11,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "var(--d-fg-dim)",
  textAlign: "right",
  padding: "8px 12px 8px 0",
  borderBottom: "1px solid var(--d-line)",
};
const tdStyle: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 12,
  fontVariantNumeric: "tabular-nums",
  color: "var(--d-fg)",
  padding: "10px 12px 10px 0",
  borderBottom: "1px solid var(--d-line)",
  textAlign: "right",
};
const nHintStyle: CSSProperties = {
  fontFamily: "var(--font-body)",
  fontSize: 11,
  color: "var(--d-fg-dim)",
};

// DUE-403: form-based text against LLM text is read over a fixed 14-day window.
const TEXT_FORMS_DAYS = 14;

function textFormsWindow(): { since: string; until: string } {
  const until = new Date();
  const since = new Date(until);
  since.setUTCDate(since.getUTCDate() - (TEXT_FORMS_DAYS - 1));
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { since: iso(since), until: iso(until) };
}

export function TextFormsPanel() {
  const { t } = useTranslation("admin");
  const lng = i18n.language || "en";
  const textFormsWin = useMemo(() => textFormsWindow(), []);
  const textFormsQuery = useTextForms(textFormsWin);
  const textForms = textFormsQuery.data;
  // Text-forms shares are compared against 5 % / 20 votes thresholds, so they always show one decimal.
  const pctFixed = (pct: number | null): string =>
    pct === null
      ? "—"
      : `${new Intl.NumberFormat(lng, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(pct)}%`;
  const verdictWord = (ok: boolean | null): string =>
    t(`usage.text_forms_verdict.${ok === null ? "few_data" : ok ? "met" : "not_met"}`);

  return (
    <div style={cardStyle} data-testid="text-forms-card">
      <div style={cardTitleStyle}>{t("usage.text_forms")}</div>
      <div style={subStyle}>{t("usage.text_forms_note", { n: TEXT_FORMS_DAYS })}</div>
      {textFormsQuery.isError ? (
        <AdminErrorState onRetry={() => void textFormsQuery.refetch()} />
      ) : textForms && (
        <>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={{ ...thStyle, textAlign: "left" }}>{t("usage.text_forms_cols.surface")}</th>
                <th style={{ ...thStyle, textAlign: "left" }}>{t("usage.text_forms_cols.locale")}</th>
                <th style={{ ...thStyle, textAlign: "left" }}>{t("usage.text_forms_cols.mode")}</th>
                <th style={thStyle}>{t("usage.text_forms_cols.readings")}</th>
                <th style={thStyle}>{t("usage.text_forms_cols.stub_pct")}</th>
                <th style={thStyle}>{t("usage.text_forms_cols.slots_filled_pct")}</th>
                <th style={thStyle}>{t("usage.text_forms_cols.relaxed_pct")}</th>
              </tr>
            </thead>
            <tbody>
              {textForms.modes.map((row) => (
                <tr
                  key={`${row.surface}-${row.locale}-${row.text_mode}`}
                  data-testid={`text-forms-mode-${row.surface}-${row.locale}-${row.text_mode}`}
                >
                  <td style={{ ...tdStyle, textAlign: "left" }}>
                    {t(`usage.text_forms_surface.${row.surface}`, { defaultValue: row.surface })}
                  </td>
                  <td style={{ ...tdStyle, textAlign: "left" }}>{row.locale}</td>
                  <td style={{ ...tdStyle, textAlign: "left" }}>
                    {t(`usage.text_forms_mode.${row.text_mode}`, { defaultValue: row.text_mode })}
                  </td>
                  <td style={tdStyle}>{formatNumber(row.readings, lng)}</td>
                  <td style={tdStyle}>{pctFixed(row.stub_pct)}</td>
                  <td style={tdStyle}>{pctFixed(row.slots_filled_pct)}</td>
                  <td style={tdStyle}>{pctFixed(row.relaxed_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {textForms.fallbacks.length > 0 && (
            <table style={{ ...tableStyle, marginTop: 16 }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, textAlign: "left" }}>{t("usage.text_forms_cols.operation")}</th>
                  <th style={{ ...thStyle, textAlign: "left" }}>{t("usage.text_forms_cols.locale")}</th>
                  <th style={{ ...thStyle, textAlign: "left" }}>{t("usage.text_forms_cols.reason")}</th>
                  <th style={{ ...thStyle, textAlign: "left" }}>{t("usage.text_forms_cols.sub_reason")}</th>
                  <th style={thStyle}>{t("usage.text_forms_cols.count")}</th>
                  <th style={thStyle}>{t("usage.text_forms_cols.attempts")}</th>
                  <th style={thStyle}>{t("usage.text_forms_cols.pct")}</th>
                </tr>
              </thead>
              <tbody>
                {textForms.fallbacks.map((row) => (
                  <tr
                    key={`${row.operation}-${row.locale}-${row.reason ?? "total"}-${row.sub_reason ?? ""}`}
                    data-testid={`text-forms-fallback-${row.operation}-${row.locale}-${row.reason ?? "total"}`}
                  >
                    <td style={{ ...tdStyle, textAlign: "left" }}>{row.operation}</td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>{row.locale}</td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>
                      {row.reason === null ? t("usage.text_forms_total") : row.reason}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>{row.sub_reason ?? "—"}</td>
                    <td style={tdStyle}>{formatNumber(row.count, lng)}</td>
                    <td style={tdStyle}>{formatNumber(row.attempts, lng)}</td>
                    <td style={tdStyle}>{pctFixed(row.pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {(textForms.votes.length > 0 || textForms.verdicts.length > 0) && (
            <>
              <div style={{ ...subStyle, marginTop: 16 }}>{t("usage.text_forms_votes_note")}</div>
              {textForms.votes.length > 0 && (
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, textAlign: "left" }}>{t("usage.text_forms_cols.operation")}</th>
                      <th style={{ ...thStyle, textAlign: "left" }}>{t("usage.text_forms_cols.locale")}</th>
                      <th style={thStyle}>{t("usage.text_forms_cols.generations")}</th>
                      <th style={thStyle}>{t("usage.text_forms_cols.up_pct")}</th>
                      <th style={thStyle}>{t("usage.text_forms_cols.cost_per_reading")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {textForms.votes.map((row) => (
                      <tr
                        key={`${row.operation}-${row.locale}`}
                        data-testid={`text-forms-votes-${row.operation}-${row.locale}`}
                      >
                        <td style={{ ...tdStyle, textAlign: "left" }}>{row.operation}</td>
                        <td style={{ ...tdStyle, textAlign: "left" }}>{row.locale}</td>
                        <td style={tdStyle}>{formatNumber(row.generations, lng)}</td>
                        <td style={tdStyle}>{pctFixed(row.up_pct)}</td>
                        <td style={tdStyle}>
                          {row.cost_per_reading === null ? "—" : `$${row.cost_per_reading.toFixed(6)}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {textForms.verdicts.length > 0 && (
                <table style={{ ...tableStyle, marginTop: 16 }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, textAlign: "left" }}>{t("usage.text_forms_cols.surface")}</th>
                      <th style={{ ...thStyle, textAlign: "left" }}>{t("usage.text_forms_cols.locale")}</th>
                      <th style={thStyle}>{t("usage.text_forms_cols.votes_ok")}</th>
                      <th style={thStyle}>{t("usage.text_forms_cols.fallback_ok")}</th>
                      <th style={thStyle}>{t("usage.text_forms_cols.stubs_ok")}</th>
                      <th style={thStyle}>{t("usage.text_forms_cols.cost_ok")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {textForms.verdicts.map((row) => (
                      <tr
                        key={`${row.surface}-${row.locale}`}
                        data-testid={`text-forms-verdict-${row.surface}-${row.locale}`}
                      >
                        <td style={{ ...tdStyle, textAlign: "left" }}>
                          {t(`usage.text_forms_surface.${row.surface}`, { defaultValue: row.surface })}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "left" }}>{row.locale}</td>
                        <td style={tdStyle}>{verdictWord(row.votes_ok)}</td>
                        <td style={tdStyle}>{verdictWord(row.fallback_ok)}</td>
                        <td style={tdStyle}>{verdictWord(row.stubs_ok)}</td>
                        <td style={tdStyle}>{verdictWord(row.cost_ok)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
          {textForms.retention.length > 0 && (
            <table style={{ ...tableStyle, marginTop: 16 }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, textAlign: "left" }}>{t("usage.text_forms_cols.locale")}</th>
                  <th style={{ ...thStyle, textAlign: "left" }}>{t("usage.text_forms_cols.mode")}</th>
                  <th style={thStyle}>{t("usage.text_forms_cols.cohort")}</th>
                  <th style={thStyle}>{t("usage.text_forms_cols.d7_pct")}</th>
                </tr>
              </thead>
              <tbody>
                {textForms.retention.map((row) => (
                  <tr
                    key={`${row.locale}-${row.text_mode}`}
                    data-testid={`text-forms-d7-${row.locale}-${row.text_mode}`}
                  >
                    <td style={{ ...tdStyle, textAlign: "left" }}>{row.locale}</td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>
                      {t(`usage.text_forms_mode.${row.text_mode}`, { defaultValue: row.text_mode })}
                    </td>
                    <td style={tdStyle}>{formatNumber(row.cohort_size, lng)}</td>
                    <td style={tdStyle}>
                      {pctFixed(row.d7_retention_pct)}{" "}
                      <span style={nHintStyle}>
                        ({formatNumber(row.d7_retained, lng)} / {formatNumber(row.d7_eligible, lng)})
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
